import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNetwork } from '@/providers/NetworkProvider';
import { ethers } from 'ethers';
import { estimateHourlyReward, readOptionalProtocolFees } from '@/utils/rewardMath';
import '@/styles/help.css';

interface HelpPanelProps {
  onClose: () => void;
}

interface EconomicsState {
  nodePrices: string[];
  chestPrices: string[];
  referralReward: string;
  rewardRatePerHour: string;
  reactivationFee: string | null;
  protocolFeeBps: number | null;
}

interface MonthlyState {
  starter: { monthlyValue: number };
  starterMax: { monthlyValue: number };
  tier3Max: { monthlyValue: number };
  tier5Max: { monthlyValue: number };
}

const formatAmount = (value: number) =>
  value >= 10 ? value.toFixed(2) : value.toFixed(3);

const formatBreakEvenMonths = (months: number) => {
  if (!Number.isFinite(months) || months <= 0) return '—';
  if (months >= 24) return `~${(months / 12).toFixed(1)} yr`;
  if (months >= 1) return `~${months.toFixed(1)} mo`;
  return `~${Math.max(1, Math.round(months * 30))} d`;
};

export const HelpPanel: React.FC<HelpPanelProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const { contracts } = useNetwork();
  const [economics, setEconomics] = React.useState<EconomicsState | null>(null);
  const [monthly, setMonthly] = React.useState<MonthlyState | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const calculateMonthlyEstimate = (
    tier: number,
    hashPower: number,
    bandwidth: number,
    uptime: number,
    basePerHour: number,
    owned = 1
  ) => {
    const hourly = estimateHourlyReward({
      rewardRatePerHour: basePerHour,
      tier,
      hashPower,
      bandwidth,
      uptime,
      owned,
    });
    return {
      hourly,
      monthlyValue: hourly * 24 * 30,
    };
  };

  React.useEffect(() => {
    let cancelled = false;

    const loadHelpData = async () => {
      if (!contracts?.gameLogic || !contracts?.nodeNFT) {
        setLoading(false);
        setError('Connect your wallet to load live prices and estimates.');
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const [
          node1,
          node2,
          node3,
          node4,
          node5,
          chest1,
          chest2,
          chest3,
          chest4,
          chest5,
          referral,
          rewardRate,
          maxTier1Hash,
          maxTier1Bw,
          maxTier1Uptime,
          maxTier3Hash,
          maxTier3Bw,
          maxTier3Uptime,
          maxTier5Hash,
          maxTier5Bw,
          maxTier5Uptime,
        ] = await Promise.all([
          contracts.gameLogic.nodePurchasePrice(),
          contracts.gameLogic.tier2NodePrice(),
          contracts.gameLogic.tier3NodePrice(),
          contracts.gameLogic.tier4NodePrice(),
          contracts.gameLogic.tier5NodePrice(),
          contracts.gameLogic.chestTierPrices(1),
          contracts.gameLogic.chestTierPrices(2),
          contracts.gameLogic.chestTierPrices(3),
          contracts.gameLogic.chestTierPrices(4),
          contracts.gameLogic.chestTierPrices(5),
          contracts.gameLogic.referralReward(),
          contracts.gameLogic.rewardRatePerHour(),
          contracts.nodeNFT.maxHashPower(1),
          contracts.nodeNFT.maxBandwidth(1),
          contracts.nodeNFT.maxUptime(1),
          contracts.nodeNFT.maxHashPower(3),
          contracts.nodeNFT.maxBandwidth(3),
          contracts.nodeNFT.maxUptime(3),
          contracts.nodeNFT.maxHashPower(5),
          contracts.nodeNFT.maxBandwidth(5),
          contracts.nodeNFT.maxUptime(5),
        ]);

        const fees = await readOptionalProtocolFees(contracts.gameLogic);

        if (cancelled) return;

        const basePerHour = Number(ethers.formatEther(rewardRate));
        setEconomics({
          nodePrices: [node1, node2, node3, node4, node5].map((v) => ethers.formatEther(v)),
          chestPrices: [chest1, chest2, chest3, chest4, chest5].map((v) => ethers.formatEther(v)),
          referralReward: ethers.formatEther(referral),
          rewardRatePerHour: ethers.formatEther(rewardRate),
          reactivationFee:
            fees.reactivationFeeWei != null
              ? ethers.formatEther(fees.reactivationFeeWei)
              : null,
          protocolFeeBps: fees.protocolFeeBps,
        });
        // Starter defaults match mint baseline (tier base stats); maxed uses on-chain caps.
        setMonthly({
          starter: calculateMonthlyEstimate(1, 10, 10, 10, basePerHour, 1),
          starterMax: calculateMonthlyEstimate(
            1,
            Number(maxTier1Hash),
            Number(maxTier1Bw),
            Number(maxTier1Uptime),
            basePerHour,
            1
          ),
          tier3Max: calculateMonthlyEstimate(
            3,
            Number(maxTier3Hash),
            Number(maxTier3Bw),
            Number(maxTier3Uptime),
            basePerHour,
            1
          ),
          tier5Max: calculateMonthlyEstimate(
            5,
            Number(maxTier5Hash),
            Number(maxTier5Bw),
            Number(maxTier5Uptime),
            basePerHour,
            1
          ),
        });
      } catch (err) {
        console.error('Failed to load economics snapshot for help panel:', err);
        if (!cancelled) {
          setError('Could not refresh live on-chain values. Guide content below is still accurate.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadHelpData();
    return () => {
      cancelled = true;
    };
  }, [contracts?.gameLogic, contracts?.nodeNFT]);

  const estimateCards = [
    { title: 'Starter Node', value: monthly?.starter.monthlyValue, note: 'T1 · base stats · BPS 100' },
    { title: 'Starter Maxed', value: monthly?.starterMax.monthlyValue, note: 'T1 · hash+bw+uptime caps' },
    { title: 'Tier 3 Maxed', value: monthly?.tier3Max.monthlyValue, note: 'T3 · BPS 500 · stat caps' },
    { title: 'Tier 5 Maxed', value: monthly?.tier5Max.monthlyValue, note: 'T5 · BPS 2500 · stat caps' },
  ];

  const breakEvenCards = monthly && economics
    ? [
        {
          label: 'Starter',
          value: formatBreakEvenMonths(Number(economics.nodePrices[0]) / monthly.starter.monthlyValue),
        },
        {
          label: 'Starter Maxed',
          value: formatBreakEvenMonths(Number(economics.nodePrices[0]) / monthly.starterMax.monthlyValue),
        },
        {
          label: 'Tier 3 Maxed',
          value: formatBreakEvenMonths(Number(economics.nodePrices[2]) / monthly.tier3Max.monthlyValue),
        },
        {
          label: 'Tier 5 Maxed',
          value: formatBreakEvenMonths(Number(economics.nodePrices[4]) / monthly.tier5Max.monthlyValue),
        },
      ]
    : [
        { label: 'Starter', value: null },
        { label: 'Starter Maxed', value: null },
        { label: 'Tier 3 Maxed', value: null },
        { label: 'Tier 5 Maxed', value: null },
      ];

  return (
    <div className="helpx-panel">
      <div className="helpx-header">
        <div className="helpx-heading">
          <span className="helpx-kicker">DOCS</span>
          <div>
            <div className="helpx-title">{t('help.title')}</div>
            <div className="helpx-subtitle">Player guide · live on-chain math</div>
          </div>
        </div>
        <div className="helpx-header-meta">
          <span className={`helpx-status-chip ${loading ? 'is-loading' : error ? 'is-error' : 'is-ready'}`}>
            {loading ? 'SYNCING…' : error ? 'OFFLINE SNAPSHOT' : 'LIVE'}
          </span>
          <button type="button" className="helpx-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>
      </div>

      <div className="helpx-content">
        <div className="helpx-guide-row">
          <section className="helpx-section">
            <h3>Quick Start</h3>
            <ol className="helpx-steps">
              <li>
                <span className="helpx-step-copy">
                  <strong>Buy</strong> a node in Shop with ETCPOWv2.
                </span>
              </li>
              <li>
                <span className="helpx-step-copy">
                  <strong>Activate</strong> it — rewards only accrue while active.
                </span>
              </li>
              <li>
                <span className="helpx-step-copy">
                  <strong>Claim</strong> before the active window ends, then reactivate.
                </span>
              </li>
              <li>
                <span className="helpx-step-copy">
                  <strong>Chests:</strong> buy → wait 1 block → open in Inventory → apply.
                </span>
              </li>
            </ol>
          </section>

          <section className="helpx-section">
            <h3>Earn More</h3>
            <ul className="helpx-tips">
              <li>Progress is intentionally slow — treat this as a long game.</li>
              <li>Higher tiers start stronger (tier BPS) and pay more.</li>
              <li>Hash, bandwidth, and uptime all multiply rewards.</li>
              <li>Fleet synergy needs upgraded bandwidth + multiple nodes.</li>
              <li>Claim before each active window expires.</li>
            </ul>
          </section>
        </div>

        <section className="helpx-section">
          <div className="helpx-section-head">
            <h3>Monthly Earnings</h3>
            <span className="helpx-section-tag">On-chain formula</span>
          </div>
          <div className="helpx-estimate-grid">
            {estimateCards.map((card) => (
              <div key={card.title} className="helpx-estimate-card">
                <div className="helpx-estimate-title">{card.title}</div>
                {loading || card.value == null ? (
                  <div className="helpx-skeleton helpx-skeleton-value" aria-hidden="true" />
                ) : (
                  <div className="helpx-estimate-value">
                    {formatAmount(card.value)}
                    <small>ETCPOWv2 / mo</small>
                  </div>
                )}
                <div className="helpx-estimate-note">{card.note}</div>
              </div>
            ))}
          </div>
          <div className="helpx-note">
            {loading ? (
              'Loading live reward rate…'
            ) : (
              <>
                Base rate <strong>{economics?.rewardRatePerHour ?? '—'}</strong> ETCPOWv2/hour ·
                includes tier BPS + hash + bandwidth + uptime (fleet bonus when multi-node + upgraded BW).
                Continuous-run estimate — Mordor windows are shorter.
              </>
            )}
          </div>
        </section>

        <section className="helpx-section">
          <div className="helpx-section-head">
            <h3>Live Prices</h3>
            <span className="helpx-section-tag">On-chain</span>
          </div>
          <div className="helpx-price-grid">
            {(economics?.nodePrices ?? ['', '', '', '', '']).map((price, index) => (
              <div key={`node-${index}`} className="helpx-price-item">
                <span>Tier {index + 1}</span>
                {loading || !economics ? (
                  <span className="helpx-skeleton helpx-skeleton-price" aria-hidden="true" />
                ) : (
                  <strong>{price}</strong>
                )}
              </div>
            ))}
            <div className="helpx-price-item">
              <span>Referral</span>
              {loading || !economics ? (
                <span className="helpx-skeleton helpx-skeleton-price" aria-hidden="true" />
              ) : (
                <strong>{economics.referralReward}</strong>
              )}
            </div>
          </div>
          <div className="helpx-note">
            {loading || !economics ? (
              'Loading chest prices…'
            ) : (
              <>
                Values in ETCPOWv2 · chests <strong>{economics.chestPrices.join(' / ')}</strong>
                {economics.reactivationFee != null && (
                  <> · reactivation <strong>{economics.reactivationFee}</strong></>
                )}
                {economics.protocolFeeBps != null && (
                  <> · protocol fee <strong>{(economics.protocolFeeBps / 100).toFixed(2)}%</strong></>
                )}
              </>
            )}
          </div>
        </section>

        <section className="helpx-section">
          <div className="helpx-section-head">
            <h3>Break-even</h3>
            <span className="helpx-section-tag">Slow game</span>
          </div>
          <div className="helpx-breakeven-grid">
            {breakEvenCards.map((card) => (
              <div key={card.label} className="helpx-price-item">
                <span>{card.label}</span>
                {loading || !card.value ? (
                  <span className="helpx-skeleton helpx-skeleton-price" aria-hidden="true" />
                ) : (
                  <strong>{card.value}</strong>
                )}
              </div>
            ))}
          </div>
          <div className="helpx-note helpx-note--warn">
            Break-even usually takes months, not days. Upgrades matter. Reactivation costs may apply.
            Rewards come from a shared pool — keep expectations grounded.
          </div>
        </section>

        <section className="helpx-section">
          <div className="helpx-section-head">
            <h3>FAQ</h3>
            <span className="helpx-section-tag">Common</span>
          </div>
          <div className="helpx-faq">
            <div className="helpx-faq-item">
              <strong>Claim daily?</strong>
              <p>No — rewards accrue while active; claim before the window expires. Daily Quests are a local habit tracker, not a faucet.</p>
            </div>
            <div className="helpx-faq-item">
              <strong>Why wait a block?</strong>
              <p>Chest loot locks to the next block so purchase + open can’t be gamed.</p>
            </div>
            <div className="helpx-faq-item">
              <strong>Upgrade later?</strong>
              <p>Yes — open chests in Inventory and apply upgrade NFTs to a node. Stats compound reward multipliers.</p>
            </div>
            <div className="helpx-faq-item">
              <strong>Season board boosts?</strong>
              <p>Cosmetic / community only until an on-chain boost contract ships — they do not change accrual yet.</p>
            </div>
          </div>
          {error && <div className="helpx-error">{error}</div>}
        </section>
      </div>
    </div>
  );
};
